import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getCompanySettings } from '@/utils/companySettings';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  ArrowLeft, Save, Eye, Download, FileText, Loader2, 
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Link as LinkIcon, Highlighter, Undo, Redo, Type, Heading1, Heading2,
  AlignLeft, AlignCenter, AlignRight, Strikethrough, Code, Quote,
  Copy, Check, User, Building2, MapPin, Calendar, Phone, Mail,
  FileSignature, Plus, Trash2, Settings, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { BrandLockup } from '@/components/brand/Brand';

const API = process.env.REACT_APP_BACKEND_URL;

// Contractor Agreement Placeholder Categories
const PLACEHOLDER_CATEGORIES = {
  contractor: {
    label: 'Contractor Information',
    icon: User,
    placeholders: [
      { key: '{{contractor.company_name}}', label: 'Company Name', description: 'Legal business name' },
      { key: '{{contractor.contact_name}}', label: 'Contact Name', description: 'Primary contact person' },
      { key: '{{contractor.address}}', label: 'Address', description: 'Business address' },
      { key: '{{contractor.city_state_zip}}', label: 'City, State, ZIP', description: 'City, state and ZIP code' },
      { key: '{{contractor.phone}}', label: 'Phone', description: 'Business phone number' },
      { key: '{{contractor.email}}', label: 'Email', description: 'Business email' },
      { key: '{{contractor.license_number}}', label: 'License Number', description: 'Contractor license number' },
      { key: '{{contractor.specialty}}', label: 'Specialty/Trade', description: 'Type of work (Electrical, Plumbing, etc.)' },
      { key: '{{contractor.tax_id}}', label: 'Tax ID/EIN', description: 'Federal tax identification number' },
    ]
  },
  designer: {
    label: 'Designer/Company',
    icon: Building2,
    placeholders: [
      { key: '{{designer.company_name}}', label: 'Company Name', description: "the tenant's own company name" },
      { key: '{{designer.representative}}', label: 'Representative', description: 'Designer representative name' },
      { key: '{{designer.address}}', label: 'Address', description: 'Company address' },
      { key: '{{designer.phone}}', label: 'Phone', description: 'Company phone' },
      { key: '{{designer.email}}', label: 'Email', description: 'Company email' },
    ]
  },
  project: {
    label: 'Project Details',
    icon: MapPin,
    placeholders: [
      { key: '{{project.name}}', label: 'Project Name', description: 'Name of the project' },
      { key: '{{project.code}}', label: 'Project Code', description: 'KC project code' },
      { key: '{{project.address}}', label: 'Project Address', description: 'Site/property address' },
      { key: '{{project.description}}', label: 'Project Description', description: 'Brief description of work' },
      { key: '{{project.scope_of_work}}', label: 'Scope of Work', description: 'Detailed scope description' },
    ]
  },
  client: {
    label: 'Client Information',
    icon: User,
    placeholders: [
      { key: '{{client.name}}', label: 'Client Name', description: 'Property owner name' },
      { key: '{{client.address}}', label: 'Client Address', description: 'Client mailing address' },
      { key: '{{client.phone}}', label: 'Client Phone', description: 'Client phone number' },
      { key: '{{client.email}}', label: 'Client Email', description: 'Client email address' },
    ]
  },
  contract: {
    label: 'Contract Terms',
    icon: FileSignature,
    placeholders: [
      { key: '{{contract.effective_date}}', label: 'Effective Date', description: 'Agreement start date' },
      { key: '{{contract.completion_date}}', label: 'Completion Date', description: 'Expected completion date' },
      { key: '{{contract.amount}}', label: 'Contract Amount', description: 'Total contract value' },
      { key: '{{contract.payment_terms}}', label: 'Payment Terms', description: 'Payment schedule/terms' },
      { key: '{{contract.retainage}}', label: 'Retainage %', description: 'Retainage percentage' },
    ]
  },
  insurance: {
    label: 'Insurance Requirements',
    icon: FileText,
    placeholders: [
      { key: '{{insurance.gl_limit}}', label: 'GL Limit', description: 'General Liability limit per occurrence' },
      { key: '{{insurance.gl_aggregate}}', label: 'GL Aggregate', description: 'General Liability aggregate' },
      { key: '{{insurance.auto_limit}}', label: 'Auto Liability', description: 'Automobile liability limit' },
      { key: '{{insurance.wc_coverage}}', label: 'Workers Comp', description: 'Workers compensation coverage' },
    ]
  },
  dates: {
    label: 'Dates & Signatures',
    icon: Calendar,
    placeholders: [
      { key: '{{today_date}}', label: "Today's Date", description: 'Current date' },
      { key: '{{current_year}}', label: 'Current Year', description: 'Current year' },
      { key: '{{signature.contractor}}', label: 'Contractor Signature', description: 'Contractor signature line' },
      { key: '{{signature.designer}}', label: 'Designer Signature', description: 'Designer signature line' },
      { key: '{{signature.date}}', label: 'Signature Date', description: 'Date of signature' },
    ]
  }
};

// Pre-built Subcontractor Agreement Template
const SUBCONTRACTOR_AGREEMENT_TEMPLATE = `<h1 style="text-align: center;">CONTRACTOR'S AGREEMENT</h1>

<p style="text-align: center;"><em>{{designer.company_name}} – Authorized Representative of Client</em></p>

<hr>

<h2>PARTIES</h2>

<p>This Agreement ("Agreement") is entered into by and between:</p>

<p><strong>DESIGNER:</strong> {{designer.company_name}}<br>
Address: {{designer.address}}<br>
Phone: {{designer.phone}}<br>
Email: {{designer.email}}<br>
Representative: {{designer.representative}}</p>

<p><strong>CONTRACTOR:</strong> {{contractor.company_name}}<br>
Address: {{contractor.address}}, {{contractor.city_state_zip}}<br>
Phone: {{contractor.phone}}<br>
Email: {{contractor.email}}<br>
License #: {{contractor.license_number}}<br>
Contact: {{contractor.contact_name}}</p>

<p><strong>CLIENT PROPERTY:</strong> {{project.address}}</p>

<p><strong>EFFECTIVE DATE:</strong> {{contract.effective_date}}</p>

<hr>

<h2>1. SCOPE OF WORK</h2>

<p>The Contractor agrees to furnish all labor, materials, equipment, and services necessary to complete the following work in a timely, workmanlike manner according to the plans, specifications, and directions provided by the Designer:</p>

<p>{{project.scope_of_work}}</p>

<h2>2. INDEPENDENT CONTRACTOR STATUS</h2>

<p>Contractor understands and acknowledges that Contractor is and shall remain an independent contractor, and neither Contractor nor any of Contractor's agents or employees shall be considered an agent or employee of Designer or Client. Contractor shall be responsible for the means and methods of the work and shall control the day-to-day activities of Contractor's employees.</p>

<h2>3. PERFORMANCE STANDARDS</h2>

<p>All Work will be completed:</p>
<ul>
  <li>In a good and workmanlike manner</li>
  <li>In accordance with all applicable federal, state, and local laws, codes, and regulations</li>
  <li>According to the plans, specifications, and directions provided by Designer</li>
  <li>Within the timeframe established by Designer in writing</li>
</ul>

<p>If any Work does not meet these standards, Designer may require Contractor to correct such defects at Contractor's expense within a reasonable period of time. If Contractor fails to make corrections, Designer may have the Work corrected by others and back-charge Contractor for all costs incurred.</p>

<h2>4. COMPLIANCE WITH LAWS</h2>

<p>Contractor shall comply with all federal, state, and local laws, codes, ordinances, and regulations applicable to the Work, including but not limited to:</p>
<ul>
  <li>Building codes and permit requirements</li>
  <li>OSHA safety regulations</li>
  <li>Environmental regulations</li>
  <li>Employment and labor laws</li>
  <li>Tax withholding and reporting requirements</li>
</ul>

<h2>5. PERMITS, FEES, AND TAXES</h2>

<p>Contractor shall be responsible for obtaining all necessary permits, paying all fees, and complying with all inspection requirements related to the Work unless otherwise specified in writing by Designer. Contractor shall also be responsible for all taxes, contributions, and assessments related to Contractor's business operations.</p>

<h2>6. INDEMNIFICATION AND HOLD HARMLESS</h2>

<p>Contractor agrees to indemnify, defend, and hold harmless Designer, Client, and their respective officers, directors, employees, and agents from and against any and all claims, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:</p>
<ul>
  <li>Contractor's performance or failure to perform the Work</li>
  <li>Contractor's violation of any law, regulation, or code</li>
  <li>Any claim by Contractor's employees, subcontractors, or suppliers</li>
  <li>Any injury to persons or damage to property caused by Contractor</li>
</ul>

<h2>7. INSURANCE REQUIREMENTS</h2>

<p>Contractor shall maintain the following insurance coverage throughout the term of this Agreement:</p>

<table style="width: 100%; border-collapse: collapse; margin: 1em 0;">
  <tr style="background-color: #f5f5f0;">
    <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Coverage Type</th>
    <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Minimum Limit</th>
  </tr>
  <tr>
    <td style="border: 1px solid #ddd; padding: 8px;">Commercial General Liability (per occurrence)</td>
    <td style="border: 1px solid #ddd; padding: 8px;">{{insurance.gl_limit}}</td>
  </tr>
  <tr>
    <td style="border: 1px solid #ddd; padding: 8px;">Commercial General Liability (aggregate)</td>
    <td style="border: 1px solid #ddd; padding: 8px;">{{insurance.gl_aggregate}}</td>
  </tr>
  <tr>
    <td style="border: 1px solid #ddd; padding: 8px;">Automobile Liability</td>
    <td style="border: 1px solid #ddd; padding: 8px;">{{insurance.auto_limit}}</td>
  </tr>
  <tr>
    <td style="border: 1px solid #ddd; padding: 8px;">Workers' Compensation</td>
    <td style="border: 1px solid #ddd; padding: 8px;">{{insurance.wc_coverage}}</td>
  </tr>
</table>

<p>Designer and Client shall be named as additional insureds on all policies. Contractor shall provide certificates of insurance upon request and shall provide ten (10) days' advance written notice of any cancellation or material change in coverage.</p>

<h2>8. MECHANICS' LIEN WAIVER</h2>

<p>Contractor agrees to provide lien waivers for all labor and materials upon receipt of each payment. Contractor shall not file or permit the filing of any mechanics' lien against the Client's property. If any such lien is filed, Contractor shall immediately take all action necessary to discharge such lien at Contractor's expense.</p>

<h2>9. PAYMENT TERMS</h2>

<p>Payment shall be made as follows:</p>
<p>{{contract.payment_terms}}</p>

<p>Designer reserves the right to withhold payment for:</p>
<ul>
  <li>Defective or incomplete work</li>
  <li>Failure to provide required lien waivers</li>
  <li>Failure to maintain required insurance</li>
  <li>Any claim or dispute regarding the Work</li>
</ul>

<h2>10. SITE CONDITIONS AND CLEAN-UP</h2>

<p>Contractor shall:</p>
<ul>
  <li>Maintain the work area in a clean and orderly condition</li>
  <li>Remove all debris and waste materials daily</li>
  <li>Protect all existing structures, finishes, and landscaping from damage</li>
  <li>Leave the premises in broom-clean condition upon completion</li>
</ul>

<h2>11. SAFETY AND CONDUCT</h2>

<p>Contractor shall:</p>
<ul>
  <li>Maintain a safe work environment at all times</li>
  <li>Ensure all personnel follow applicable safety regulations</li>
  <li>Prohibit the use of alcohol, drugs, or any controlled substances on the job site</li>
  <li>Conduct all work in a professional manner</li>
</ul>

<h2>12. WARRANTY</h2>

<p>Contractor warrants all Work for a period of <strong>one (1) year</strong> from the date of completion. During this warranty period, Contractor shall correct any defects in materials or workmanship at no additional cost to Designer or Client.</p>

<h2>13. TERMINATION</h2>

<p>Either party may terminate this Agreement upon written notice if the other party:</p>
<ul>
  <li>Materially breaches any provision of this Agreement</li>
  <li>Fails to cure such breach within ten (10) days of written notice</li>
  <li>Becomes insolvent or files for bankruptcy</li>
</ul>

<p>Designer may terminate this Agreement for convenience upon five (5) days' written notice. In such case, Contractor shall be paid for all Work satisfactorily completed prior to termination.</p>

<h2>14. DISPUTE RESOLUTION</h2>

<p>Any dispute arising out of or relating to this Agreement shall be resolved through good faith negotiation. If negotiation fails, the parties agree to submit to binding arbitration in accordance with the rules of the American Arbitration Association. The prevailing party shall be entitled to recover reasonable attorneys' fees and costs.</p>

<h2>15. GOVERNING LAW</h2>

<p>This Agreement shall be governed by and construed in accordance with the laws of the State of Missouri. Any legal action arising out of this Agreement shall be filed in the courts of Greene County, Missouri.</p>

<h2>16. ENTIRE AGREEMENT</h2>

<p>This Agreement constitutes the entire agreement between the parties and supersedes all prior negotiations, representations, or agreements relating to the subject matter hereof. This Agreement may be amended only in writing signed by both parties.</p>

<hr>

<h2>SIGNATURES</h2>

<table style="width: 100%; margin-top: 2em;">
  <tr>
    <td style="width: 48%; vertical-align: top;">
      <p><strong>CONTRACTOR:</strong></p>
      <p style="border-bottom: 1px solid #000; height: 40px; margin-bottom: 5px;">{{signature.contractor}}</p>
      <p>Name: {{contractor.contact_name}}</p>
      <p>Title: ________________________</p>
      <p>Date: {{signature.date}}</p>
    </td>
    <td style="width: 4%;"></td>
    <td style="width: 48%; vertical-align: top;">
      <p><strong>DESIGNER:</strong></p>
      <p style="border-bottom: 1px solid #000; height: 40px; margin-bottom: 5px;">{{signature.designer}}</p>
      <p>Name: {{designer.representative}}</p>
      <p>Title: ________________________</p>
      <p>Date: {{signature.date}}</p>
    </td>
  </tr>
</table>

<p style="text-align: center; margin-top: 3em; color: #666; font-size: 0.9em;">
  <em>Engineered by KEY-MADE — Managing Architectural Design Execution</em><br>
  © {{current_year}} {{designer.company_name}}. All rights reserved.
</em></p>`;

// TipTap Editor Toolbar Button
const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
  <Button
    type="button"
    variant="ghost"
    size="icon"
    className={`h-8 w-8 ${isActive ? 'bg-[#70170F]/10 text-[#70170F]' : ''}`}
    onClick={onClick}
    disabled={disabled}
    title={title}
  >
    {children}
  </Button>
);

export default function ContractorAgreementEditor() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const contractorId = searchParams.get('contractor');
  const projectId = searchParams.get('project');
  
  const [template, setTemplate] = useState({
    name: 'Subcontractor Agreement',
    description: 'Standard agreement for subcontractors',
    category: 'contract',
    content: '',
    requires_signature: true,
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contractor, setContractor] = useState(null);
  const [project, setProject] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(true);
  const [copiedPlaceholder, setCopiedPlaceholder] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  // DP63 Part 1a — the tenant's OWN details. These five placeholders were filled
  // with literals ('Keystone Creative, LLC', 'Chad Holgerson', 'Springfield, MO',
  // '(417) 555-0100', 'chad@keystonecreative.design'), so any firm generating an
  // agreement issued it in Chad's name with his phone number in the signature.
  const [designerSettings, setDesignerSettings] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState(contractorId || '');
  const [selectedProject, setSelectedProject] = useState(projectId || '');
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved === 'true';
  });

  // DP63 Part 1a — resolve the tenant's own details once.
  useEffect(() => {
    let alive = true;
    getCompanySettings()
      .then((d) => { if (alive) setDesignerSettings(d || {}); })
      .catch(() => { if (alive) setDesignerSettings({}); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const handleDarkModeChange = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved === 'true');
    };
    window.addEventListener('storage', handleDarkModeChange);
    const interval = setInterval(handleDarkModeChange, 2000);
    return () => {
      window.removeEventListener('storage', handleDarkModeChange);
      clearInterval(interval);
    };
  }, []);
  
  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      Highlight,
      Placeholder.configure({
        placeholder: 'Start typing your agreement content...',
      }),
    ],
    content: SUBCONTRACTOR_AGREEMENT_TEMPLATE,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[600px] p-4',
      },
    },
  });
  
  // Load template if editing
  useEffect(() => {
    if (templateId && templateId !== 'new') {
      loadTemplate();
    }
  }, [templateId]);
  
  // Load contractors and projects for generation
  useEffect(() => {
    loadContractors();
    loadProjects();
  }, []);
  
  // Load specific contractor if provided
  useEffect(() => {
    if (contractorId) {
      loadContractor(contractorId);
    }
  }, [contractorId]);
  
  // Load specific project if provided
  useEffect(() => {
    if (projectId) {
      loadProject(projectId);
    }
  }, [projectId]);
  
  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/document-templates/${templateId}`);
      const data = response.data;
      setTemplate({
        name: data.name || 'Subcontractor Agreement',
        description: data.description || '',
        category: data.category || 'contract',
        content: data.content || '',
        requires_signature: data.requires_signature ?? true,
      });
      if (data.content && editor) {
        editor.commands.setContent(data.content);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };
  
  const loadContractors = async () => {
    try {
      const response = await axios.get(`${API}/api/contractors`);
      setContractors(response.data || []);
    } catch (error) {
      console.error('Error loading contractors:', error);
    }
  };
  
  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/api/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };
  
  const loadContractor = async (id) => {
    try {
      const response = await axios.get(`${API}/api/contractors/${id}`);
      setContractor(response.data);
      setSelectedContractor(id);
    } catch (error) {
      console.error('Error loading contractor:', error);
    }
  };
  
  const loadProject = async (id) => {
    try {
      const response = await axios.get(`${API}/api/projects/${id}`);
      setProject(response.data);
      setSelectedProject(id);
    } catch (error) {
      console.error('Error loading project:', error);
    }
  };
  
  // Insert placeholder at cursor position
  const insertPlaceholder = (placeholder) => {
    if (editor) {
      editor.chain().focus().insertContent(placeholder).run();
      toast.success(`Inserted ${placeholder}`);
    }
  };
  
  // Copy placeholder to clipboard
  const copyPlaceholder = (placeholder) => {
    navigator.clipboard.writeText(placeholder);
    setCopiedPlaceholder(placeholder);
    setTimeout(() => setCopiedPlaceholder(null), 2000);
    toast.success('Copied to clipboard');
  };
  
  // Save template
  const saveTemplate = async () => {
    if (!template.name) {
      toast.error('Template name is required');
      return;
    }
    
    try {
      setSaving(true);
      const content = editor?.getHTML() || '';
      
      const payload = {
        name: template.name,
        description: template.description,
        category: template.category,
        content: content,
        requires_signature: template.requires_signature,
        template_type: 'contractor_agreement',
      };
      
      if (templateId && templateId !== 'new') {
        await axios.put(`${API}/api/document-templates/${templateId}`, payload);
        toast.success('Template updated successfully');
      } else {
        const response = await axios.post(`${API}/api/document-templates/create`, payload);
        toast.success('Template created successfully');
        navigate(`/documents/contractor-agreement/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };
  
  // Generate preview with real data
  const generatePreview = () => {
    let content = editor?.getHTML() || '';
    
    // Replace with contractor data
    if (contractor) {
      content = content.replace(/\{\{contractor\.company_name\}\}/g, contractor.company_name || '[Company Name]');
      content = content.replace(/\{\{contractor\.contact_name\}\}/g, contractor.contact_name || '[Contact Name]');
      content = content.replace(/\{\{contractor\.address\}\}/g, contractor.address?.street || '[Address]');
      content = content.replace(/\{\{contractor\.city_state_zip\}\}/g, 
        `${contractor.address?.city || ''}, ${contractor.address?.state || ''} ${contractor.address?.zip || ''}`.trim() || '[City, State ZIP]');
      content = content.replace(/\{\{contractor\.phone\}\}/g, contractor.phone || '[Phone]');
      content = content.replace(/\{\{contractor\.email\}\}/g, contractor.email || '[Email]');
      content = content.replace(/\{\{contractor\.license_number\}\}/g, contractor.license_number || '[License #]');
      content = content.replace(/\{\{contractor\.specialty\}\}/g, contractor.specialty || contractor.contractor_type || '[Specialty]');
    }
    
    // Replace with project data
    if (project) {
      content = content.replace(/\{\{project\.name\}\}/g, project.name || '[Project Name]');
      content = content.replace(/\{\{project\.code\}\}/g, project.project_code || '[Project Code]');
      content = content.replace(/\{\{project\.address\}\}/g, 
        project.site_address ? `${project.site_address.street || ''}, ${project.site_address.city || ''}, ${project.site_address.state || ''} ${project.site_address.zip_code || ''}`.trim() : '[Project Address]');
      content = content.replace(/\{\{project\.description\}\}/g, project.description || '[Description]');
      content = content.replace(/\{\{project\.scope_of_work\}\}/g, project.scope?.scope_of_work?.join(', ') || '[Scope of Work]');
      
      // Client data from project
      content = content.replace(/\{\{client\.name\}\}/g, project.client?.name || '[Client Name]');
      content = content.replace(/\{\{client\.email\}\}/g, project.client?.email || '[Client Email]');
      content = content.replace(/\{\{client\.phone\}\}/g, project.client?.phone || '[Client Phone]');
    }
    
    // Replace system placeholders
    const today = new Date();
    content = content.replace(/\{\{today_date\}\}/g, today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
    content = content.replace(/\{\{current_year\}\}/g, today.getFullYear().toString());
    
    // DP63 Part 1a — the tenant's own values, from GET /api/company-settings.
    // A field the tenant has not set renders as an EMPTY STRING: a blank in the
    // document, never another tenant's detail.
    const ds = designerSettings || {};
    const designerAddress = [ds.address, [ds.city, ds.state].filter(Boolean).join(', ')]
      .filter(Boolean).join(' ').trim();
    content = content.replace(/\{\{designer\.company_name\}\}/g, ds.company_name || '');
    content = content.replace(/\{\{designer\.representative\}\}/g, ds.principal_designer_name || '');
    content = content.replace(/\{\{designer\.address\}\}/g, designerAddress);
    content = content.replace(/\{\{designer\.phone\}\}/g, ds.company_phone || ds.phone || '');
    content = content.replace(/\{\{designer\.email\}\}/g, ds.company_email || ds.email || '');
    
    // Replace contract defaults
    content = content.replace(/\{\{contract\.effective_date\}\}/g, today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
    content = content.replace(/\{\{insurance\.gl_limit\}\}/g, '$500,000');
    content = content.replace(/\{\{insurance\.gl_aggregate\}\}/g, '$1,000,000');
    content = content.replace(/\{\{insurance\.auto_limit\}\}/g, '$500,000');
    content = content.replace(/\{\{insurance\.wc_coverage\}\}/g, 'Statutory Limits');
    
    // Signature placeholders
    content = content.replace(/\{\{signature\.contractor\}\}/g, '');
    content = content.replace(/\{\{signature\.designer\}\}/g, '');
    content = content.replace(/\{\{signature\.date\}\}/g, '________________');
    
    // Replace any remaining placeholders with placeholder text
    content = content.replace(/\{\{[^}]+\}\}/g, (match) => `<span style="background-color: #fef3c7; padding: 2px 4px; border-radius: 2px;">${match}</span>`);
    
    return content;
  };
  
  // Generate document for specific contractor/project
  const generateDocument = async () => {
    if (!selectedContractor) {
      toast.error('Please select a contractor');
      return;
    }
    
    try {
      setSaving(true);
      
      // Load fresh data
      const contractorRes = await axios.get(`${API}/api/contractors/${selectedContractor}`);
      setContractor(contractorRes.data);
      
      if (selectedProject) {
        const projectRes = await axios.get(`${API}/api/projects/${selectedProject}`);
        setProject(projectRes.data);
      }
      
      setShowGenerateDialog(false);
      setShowPreview(true);
      toast.success('Document generated! Review the preview.');
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error('Failed to generate document');
    } finally {
      setSaving(false);
    }
  };
  
  // Dark mode classes
  const bgClass = darkMode ? 'bg-black' : 'bg-[#F8F7F5]';
  const headerClass = darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-border';
  const cardClass = darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-none';
  const inputClass = darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : '';
  const textClass = darkMode ? 'text-[#E5E2DD]' : '';
  
  if (loading) {
    return (
      <div className={`min-h-screen ${bgClass} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#BC5D41]" />
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${bgClass}`}>
      {/* Header */}
      <header className={`${headerClass} border-b sticky top-0 z-50`}>
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          {/* Breadcrumb */}
          <nav className={`flex items-center gap-2 text-sm mb-3 ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
            <button onClick={() => navigate('/dashboard')} className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : ''}`}>
              My Desk
            </button>
            <span>/</span>
            <button onClick={() => navigate('/documents')} className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : ''}`}>
              Document Library
            </button>
            <span>/</span>
            <span className={textClass}>Contractor Agreement</span>
          </nav>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/documents')} className={darkMode ? 'hover:bg-[#1A1A1A] text-[#8A8A8A]' : ''}>
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Back
              </Button>
              <div className={`h-6 w-px ${darkMode ? 'bg-[#2A2A2A]' : 'bg-border'}`} />
              <div>
                <h1 className={`text-xl font-heading ${textClass}`}>
                  Contractor Agreement Editor
                </h1>
                <span className={`flex items-baseline gap-1`}>
                  <BrandLockup onDark={darkMode} testId="agreement-brand" />
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowGenerateDialog(true)} 
                className={darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : ''}
                data-testid="generate-document-btn"
              >
                <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Generate for Contractor
              </Button>
              <Button variant="outline" onClick={() => setShowPreview(true)} className={darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : ''}>
                <Eye className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Preview
              </Button>
              <Button onClick={saveTemplate} disabled={saving} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white" data-testid="save-template-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                Save Template
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar: Settings & Placeholders */}
          <div className="col-span-3 space-y-4">
            {/* Template Settings */}
            <Card className={`${cardClass} shadow-sm`}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-sm flex items-center gap-2 ${textClass}`}>
                  <Settings className="w-4 h-4" />
                  Template Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className={`text-xs ${textClass}`}>Template Name</Label>
                  <Input
                    value={template.name}
                    onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                    className={`h-8 text-sm ${inputClass}`}
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <Label className={`text-xs ${textClass}`}>Description</Label>
                  <Textarea
                    value={template.description}
                    onChange={(e) => setTemplate(prev => ({ ...prev, description: e.target.value }))}
                    className={`text-sm min-h-[60px] ${inputClass}`}
                    rows={2}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className={`text-xs ${textClass}`}>Requires Signature</Label>
                  <Switch
                    checked={template.requires_signature}
                    onCheckedChange={(checked) => setTemplate(prev => ({ ...prev, requires_signature: checked }))}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Placeholder Library */}
            <Card className={`${cardClass} shadow-sm`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-sm ${textClass}`}>Insert Placeholders</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowPlaceholders(!showPlaceholders)}
                    className="h-6 w-6 p-0"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${showPlaceholders ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
                <CardDescription className="text-xs">
                  Click to insert at cursor position
                </CardDescription>
              </CardHeader>
              {showPlaceholders && (
                <CardContent className="pt-0 max-h-[500px] overflow-y-auto">
                  <div className="space-y-3">
                    {Object.entries(PLACEHOLDER_CATEGORIES).map(([catKey, category]) => (
                      <Collapsible key={catKey} defaultOpen={catKey === 'contractor'}>
                        <CollapsibleTrigger className={`flex items-center gap-2 w-full text-left text-sm font-medium py-1 ${textClass}`}>
                          <category.icon className="w-4 h-4 text-[#BC5D41]" />
                          {category.label}
                          <ChevronDown className="w-3 h-3 ml-auto" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-1 pl-6 space-y-1">
                          {category.placeholders.map((p) => (
                            <div 
                              key={p.key}
                              className={`group flex items-center justify-between p-1.5 rounded text-xs cursor-pointer transition-colors ${
                                darkMode ? 'hover:bg-[#1A1A1A]' : 'hover:bg-gray-50'
                              }`}
                              onClick={() => insertPlaceholder(p.key)}
                            >
                              <div className="flex-1 min-w-0">
                                <span className={`font-mono text-[#BC5D41] text-[10px] block truncate`}>{p.key}</span>
                                <span className={`${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'} text-[10px]`}>{p.label}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 opacity-0 group-hover:opacity-100 flex-shrink-0"
                                onClick={(e) => { e.stopPropagation(); copyPlaceholder(p.key); }}
                              >
                                {copiedPlaceholder === p.key ? (
                                  <Check className="w-3 h-3 text-green-500" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </Button>
                            </div>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
          
          {/* Main Editor Area */}
          <div className="col-span-9">
            <Card className={`${cardClass} shadow-sm`}>
              {/* Editor Toolbar */}
              <div className={`border-b px-4 py-2 flex items-center gap-1 flex-wrap ${darkMode ? 'border-[#2A2A2A] bg-[#0D0D0D]' : 'bg-gray-50'}`}>
                {/* Text Formatting */}
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  isActive={editor?.isActive('bold')}
                  title="Bold"
                >
                  <Bold className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  isActive={editor?.isActive('italic')}
                  title="Italic"
                >
                  <Italic className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleUnderline().run()}
                  isActive={editor?.isActive('underline')}
                  title="Underline"
                >
                  <UnderlineIcon className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleStrike().run()}
                  isActive={editor?.isActive('strike')}
                  title="Strikethrough"
                >
                  <Strikethrough className="w-4 h-4" />
                </ToolbarButton>
                
                <Separator orientation="vertical" className="h-6 mx-1" />
                
                {/* Headings */}
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                  isActive={editor?.isActive('heading', { level: 1 })}
                  title="Heading 1"
                >
                  <Heading1 className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                  isActive={editor?.isActive('heading', { level: 2 })}
                  title="Heading 2"
                >
                  <Heading2 className="w-4 h-4" />
                </ToolbarButton>
                
                <Separator orientation="vertical" className="h-6 mx-1" />
                
                {/* Lists */}
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                  isActive={editor?.isActive('bulletList')}
                  title="Bullet List"
                >
                  <List className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                  isActive={editor?.isActive('orderedList')}
                  title="Numbered List"
                >
                  <ListOrdered className="w-4 h-4" />
                </ToolbarButton>
                
                <Separator orientation="vertical" className="h-6 mx-1" />
                
                {/* Block Elements */}
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                  isActive={editor?.isActive('blockquote')}
                  title="Quote"
                >
                  <Quote className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().toggleHighlight().run()}
                  isActive={editor?.isActive('highlight')}
                  title="Highlight"
                >
                  <Highlighter className="w-4 h-4" />
                </ToolbarButton>
                
                <Separator orientation="vertical" className="h-6 mx-1" />
                
                {/* Undo/Redo */}
                <ToolbarButton
                  onClick={() => editor?.chain().focus().undo().run()}
                  disabled={!editor?.can().undo()}
                  title="Undo"
                >
                  <Undo className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().redo().run()}
                  disabled={!editor?.can().redo()}
                  title="Redo"
                >
                  <Redo className="w-4 h-4" />
                </ToolbarButton>
              </div>
              
              {/* Editor Content */}
              <CardContent className="p-0">
                <div className={`min-h-[700px] ${darkMode ? 'bg-[#1A1A1A]' : 'bg-white'}`}>
                  <EditorContent 
                    editor={editor} 
                    className={`
                      ${darkMode ? 'prose-invert' : ''}
                      [&_.ProseMirror]:min-h-[700px]
                      [&_.ProseMirror]:p-6
                      [&_.ProseMirror]:focus:outline-none
                      [&_.ProseMirror_h1]:text-2xl
                      [&_.ProseMirror_h1]:font-bold
                      [&_.ProseMirror_h1]:mb-4
                      [&_.ProseMirror_h2]:text-xl
                      [&_.ProseMirror_h2]:font-semibold
                      [&_.ProseMirror_h2]:mb-3
                      [&_.ProseMirror_h2]:mt-6
                      [&_.ProseMirror_p]:mb-3
                      [&_.ProseMirror_ul]:list-disc
                      [&_.ProseMirror_ul]:pl-6
                      [&_.ProseMirror_ul]:mb-3
                      [&_.ProseMirror_ol]:list-decimal
                      [&_.ProseMirror_ol]:pl-6
                      [&_.ProseMirror_ol]:mb-3
                      [&_.ProseMirror_li]:mb-1
                      [&_.ProseMirror_table]:w-full
                      [&_.ProseMirror_table]:border-collapse
                      [&_.ProseMirror_th]:border
                      [&_.ProseMirror_th]:p-2
                      [&_.ProseMirror_th]:bg-gray-100
                      [&_.ProseMirror_td]:border
                      [&_.ProseMirror_td]:p-2
                      [&_.ProseMirror_hr]:my-6
                      [&_.ProseMirror_blockquote]:border-l-4
                      [&_.ProseMirror_blockquote]:border-gray-300
                      [&_.ProseMirror_blockquote]:pl-4
                      [&_.ProseMirror_blockquote]:italic
                    `}
                    data-testid="editor-content"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      {/* Generate Document Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className={darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}>
          <DialogHeader>
            <DialogTitle className={textClass}>Generate Agreement for Contractor</DialogTitle>
            <DialogDescription>
              Select a contractor and optionally a project to auto-fill the agreement
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className={textClass}>Contractor *</Label>
              <Select value={selectedContractor} onValueChange={setSelectedContractor}>
                <SelectTrigger className={inputClass} data-testid="contractor-select">
                  <SelectValue placeholder="Select a contractor..." />
                </SelectTrigger>
                <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                  {contractors.map(c => (
                    <SelectItem key={c.id} value={c.id} className={darkMode ? 'text-[#E5E2DD] focus:bg-[#2A2A2A]' : ''}>
                      {c.company_name} ({c.contractor_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className={textClass}>Project (Optional)</Label>
              <Select value={selectedProject || 'none'} onValueChange={(v) => setSelectedProject(v === 'none' ? '' : v)}>
                <SelectTrigger className={inputClass} data-testid="project-select">
                  <SelectValue placeholder="Select a project..." />
                </SelectTrigger>
                <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                  <SelectItem value="none" className={darkMode ? 'text-[#E5E2DD] focus:bg-[#2A2A2A]' : ''}>
                    No specific project
                  </SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id} className={darkMode ? 'text-[#E5E2DD] focus:bg-[#2A2A2A]' : ''}>
                      {p.name} ({p.project_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)} className={darkMode ? 'border-[#2A2A2A]' : ''}>
              Cancel
            </Button>
            <Button onClick={generateDocument} disabled={!selectedContractor || saving} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Generate Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className={textClass}>Document Preview</DialogTitle>
            <DialogDescription>
              {contractor ? `Preview for ${contractor.company_name}` : 'Preview with sample data'}
            </DialogDescription>
          </DialogHeader>
          
          <div className={`flex-1 overflow-y-auto p-6 rounded-lg ${darkMode ? 'bg-white text-black' : 'bg-white'}`}>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: generatePreview() }}
            />
          </div>
          
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setShowPreview(false)} className={darkMode ? 'border-[#2A2A2A]' : ''}>
              Close
            </Button>
            <Button className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Export PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
